import React, { useEffect, useState } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelPageProps } from "../../interfaces";
import { SentinelBasePage } from "../sentinel-base";
import {
  Box,
  Button,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from "@cloudscape-design/components";
import { getOnboardedTeams } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { OnboardedTeam, OnboardedTeamResponse } from "src/core/sentinel/types";

import { useNavigate } from "react-router-dom";

export interface SentinelTeamProfilePageProps extends SentinelPageProps {
  readOnly?: boolean;
  sentinelController: SentinelController;
}
export const TeamProfilePage: React.FC<SentinelTeamProfilePageProps> = (
  // eslint-disable-next-line
  props: SentinelTeamProfilePageProps
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [alias, setAlias] = useState("");
  const [onboardedTeam, setOnboardedTeam] = useState<OnboardedTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [filteringText, setFilteringText] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const pageSize = 50;
  const filteredTeams = onboardedTeam.filter((team) => {
    const content = [
      team.teamName,
      team.teamEmail,
      team.teamId,
      team.requesterId,
    ]
      .join(" ")
      .toLowerCase();

    return (
      !filteringText || content.includes(filteringText.toLowerCase())
    );
  });

  const paginatedItems = filteredTeams.slice(
    (currentPageIndex - 1) * pageSize,
    currentPageIndex * pageSize
  );

  const totalPages = Math.ceil(filteredTeams.length / pageSize);
  useEffect(() => {
    setAlias(props.sentinelController.userIdentity.username);
  }, [props.sentinelController.userIdentity.username]);

  useEffect(() => {
    dispatch(setPageTitle("Team Profile"));
    dispatch(removeInfoMessage({ id: 'message_1' }));
  }, []);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await dispatch(
          getOnboardedTeams({
            sentinelController: props.sentinelController,
          })
        );
        const teams = (response as OnboardedTeamResponse)?.payload?.payload?.onboardedTeams;
        setOnboardedTeam(teams);
        setLoading(false);
      } catch (error) {
        console.error(`Error for gettinng onboarded team: ${error}`, error);
      }
    };
    fetchTeam();
  }, []);
  
  return (
    <SentinelBasePage>
      <Table
        items={paginatedItems}
        columnDefinitions={[
          {
            id: "teamName",
            header: "Team Name",
            cell: (e: OnboardedTeam) => e.teamName,
            sortingField: "teamName",
            isRowHeader: true,
          },
          {
            id: "teamId",
            header: "Team ID",
            cell: (e: OnboardedTeam) => e.teamId,
            sortingField: "teamId",
          },
          {
            id: "teamEmail",
            header: "Team Email",
            cell: (e: OnboardedTeam) => e.teamEmail,
            sortingField: "teamEmail",
          },
          {
            id: "requesterId",
            header: "Requester ID",
            cell: (e: OnboardedTeam) => e.requesterId,
            sortingField: "requesterId",
          },
          {
            id: "actions",
            header: "Actions",
            cell: (e: OnboardedTeam) => (
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="primary"
                  onClick={() => navigate(`/team/details/${encodeURIComponent(e.teamName)}`)}
                >
                  Packages Onboarded
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate(`/cr-dashboard/${encodeURIComponent(e.teamName)}`)}
                >
                  CR Dashboard
                </Button>
              </SpaceBetween>
            ),
          },
        ]}
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        loading={loading}
        loadingText="Loading resources"
        empty={
          <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <b>No teams found</b>
              <Button>Create team</Button>
            </SpaceBetween>
          </Box>
        }
        filter={
          <TextFilter
            filteringPlaceholder="Find team"
            filteringText={filteringText}
            onChange={({ detail }) => {
              setFilteringText(detail.filteringText);
              setCurrentPageIndex(1);
            }}
            countText={`${filteredTeams.length} matches`}
          />
        }
        header={<Header>Onboarded Teams</Header>}
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) =>
              setCurrentPageIndex(detail.currentPageIndex)
            }
          />
        }
      />
    </SentinelBasePage>
  );
};
